<template>
  <div>
    <!-- Button that opens dropdown -->
    <div
      :id="buttonId"
      v-tooltip.right="{
        value: 'Switch look',
        hideDelay: 125,
        showDelay: 1,
      }"
      class="am3-optin-panel-trigger navbar-item size-[36px] bg-surface hover:bg-brand-light hover:text-brand cursor-pointer flex flex-col items-center justify-center mb-4 rounded-full"
      :class="isPanelOpen ? 'text-brand' : '' "
      data-test-id="early-access-opt-in-panel-trigger"
      @click.stop="togglePanel"
    >
      <AM3Icon
        type="sparkles"
        size="md"
      />
    </div>

    <!-- Dropdown panel -->
    <AM3DropdownPanel
      id="am3-optin-panel"
      v-model="isPanelOpen"
      :target="buttonId"
      position="right"
      fit-content
    >
      <div class="p-4 sm:px-8 sm:py-6 flex flex-col items-start optin-in-panel-content w-[420px] max-w-[95vw] sm:max-w-[480px]  ">
        <div class="flex flex-row items-center justify-between w-full">
          <!-- Early Access Badge -->
          <div class="inline-flex flex-row" />

          <AM3Icon
            type="x-mark"
            size="sm"
            class="cursor-pointer text-surface-light hover:text-surface-strong"
            data-test-id="early-access-opt-in-panel-close"
            @click="closePanel"
          />
        </div>

        <!-- Content -->
        <div class="text-sm leading-5 mb-6">
          <h2 class="text-2xl font-bold mb-8">
            The new look is here
          </h2>
          <p class="text-sm mb-4">
            Audience, Events & Data Capture (formerly Campaigns) have been redesigned - faster, more intuitive, and built around how you actually work. This is the first of many updates coming, including significant new features that have been months in the making.
          </p>
          <p class="text-sm ">
            Not ready yet? Switch back to classic view anytime here or in your Settings.
          </p>
        </div>

        <!-- Toggle -->
        <div class="flex items-center flex-row w-full justify-start">
          <span class="text-sm font-medium inline-block mr-4">Enable the new look</span>
          <AM3Toggle
            :model-value="isOptedIn"
            data-test-id="early-access-toggle"
            @update:model-value="toggleOptIn"
          />
        </div>
      </div>
    </AM3DropdownPanel>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import AM3DropdownPanel from '@/components/am3-generic/base/AM3DropdownPanel.vue'
import AM3Toggle from '@/components/am3-generic/inputs/selection/AM3Toggle.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import { getCookie, setCookie } from '@/utils/cookie'

// Generate unique ID for button
const buttonId = `early-access-trigger-${uuidv4()}`

// State
const isPanelOpen = ref(false)
const isOptedIn = ref(false)
const imagePath = '@/client/assets/images/audience/am3-optin-promo.png'

// Toggle panel visibility
const togglePanel = () => {
  isPanelOpen.value = !isPanelOpen.value
}

// Mark the user as notified (set cookie so panel won't auto-open again)
const markAsNotified = () => {
  const cookieOptions = {
    domain: `.${window.location.hostname.split('.').slice(-2).join('.')}`,
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
  }
  setCookie('AM3-optin-notified', 'true', cookieOptions)
}

// Close panel
const closePanel = () => {
  isPanelOpen.value = false
  markAsNotified()
}

// Handle opt-in toggle
const toggleOptIn = (value: boolean) => {
  isOptedIn.value = value
  markAsNotified()

  const cookieOptions = {
    domain: `.${window.location.hostname.split('.').slice(-2).join('.')}`,
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
  }

  // Set cookie based on the toggle state
  if (value) {
    // Clear opt-out cookie to opt-in (no expiration needed)
    setCookie('am3EarlyAccessOptOut', '', { ...cookieOptions, maxAge: 0 })
  }
  else {
    // Set opt-out cookie with 7-day expiration
    setCookie('am3EarlyAccessOptOut', 'true', {
      ...cookieOptions,
      maxAge: 604800,
    })
    // Add a short delay to ensure cookie is written before reload
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }
}

// On mount, check if the user has opted out (if not opted out, then they are opted in)
// Also auto-open the panel once if the user hasn't been notified yet
onMounted(() => {
  const cookieValue = getCookie('am3EarlyAccessOptOut')
  isOptedIn.value = cookieValue !== 'true'

  const hasBeenNotified = getCookie('AM3-optin-notified')
  if (!hasBeenNotified && !isOptedIn.value) {
    isPanelOpen.value = true
  }
})
</script>

<style>
.am3-optin-panel-trigger {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, border-color 0.2s;
}

.am3-optin-panel-trigger:hover {
  background-color: var(--border-color-subtle);
}

#am3-optin-panel {
  transform: translateX(25px);
  max-width: 480px ;
  @media ( min-height: 697px ) {
    bottom: 11.75rem;
    top: auto !important ; /* we need to overwrite the inline style tag, so !important it is. */
  }
}
</style>
